import type { ConversationMessageModel, ConversationModel } from "#features/conversations/types/ConversationMessageModel.ts";
import type { FileModel } from "#features/conversations/types/FileModel.ts";
import { ThinkingStepCodes, ThinkingStepStates } from "#features/conversations/types/ThinkingStep.ts";
import { AiQueryKeys } from "#lib/AiQueryKeys.ts";
import type { PageActionModel } from "#types/PageActionModel.ts";
import type { ConversationMessageFile } from "#types/types.ts";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import { v4 as uuidv4 } from "uuid";

export const useConversationMessageCacheHandler = () => {
    const queryClient = useQueryClient();

    const addMessage = useCallback((conversationId: string, message: ConversationMessageModel) => {
        queryClient.setQueryData<ConversationModel>(AiQueryKeys.GetConversation(conversationId), old => {
            return { ...old!, messages: [...old!.messages, message] };
        });
    }, [queryClient]);

    const addUserMessage = useCallback((conversationId: string, content: string) => {
        const userMessage: ConversationMessageModel = {
            id: uuidv4(),
            timestamp: new Date().toISOString(),
            content,
            role: "user",
            assistantStatus: "idle",
            sources: [],
            isBlocked: false
        };

        addMessage(conversationId, userMessage);
    }, [addMessage]);

    const addFileMessage = useCallback((conversationId: string, files: FileModel[]) => {
        if (files.length === 0) {
            return;
        }

        const uploadedFiles = files
            .map<ConversationMessageFile>(file => ({
                id: file.id,
                fileName: file.fileName,
                fileUploadStatus: file.fileUploadStatus
            }));

        const fileMessage: ConversationMessageModel = {
            id: uuidv4(),
            timestamp: new Date().toISOString(),
            content: "",
            role: "tool",
            toolType: "file_upload",
            assistantStatus: "idle",
            sources: [],
            isBlocked: false,
            files: uploadedFiles
        };

        addMessage(conversationId, fileMessage);
    }, [addMessage]);

    const addTargetedReplyMessage = useCallback((conversationId: string, targetedReply?: string) => {
        if (!targetedReply) {
            return;
        }

        const targetedReplyMessage: ConversationMessageModel = {
            id: uuidv4(),
            timestamp: new Date().toISOString(),
            content: targetedReply,
            role: "tool",
            toolType: "targeted_reply",
            assistantStatus: "idle",
            sources: []
        };

        addMessage(conversationId, targetedReplyMessage);
    }, [addMessage]);

    const addAssistantMessage = useCallback((conversationId: string, messageId: string, files?: FileModel[], pageActions?: PageActionModel[]) => {
        const assistantMessage: ConversationMessageModel = {
            id: messageId,
            timestamp: new Date().toISOString(),
            content: "",
            role: "assistant",
            sources: [],
            assistantStatus: "thinking",
            isBlocked: false,
            files,
            pageActions,
            pageActionResponse: null,
            generatedFiles: [],
            thinkingSteps: [
                {
                    id: "thinking-1",
                    code: ThinkingStepCodes.thinking,
                    state: ThinkingStepStates.active,
                    sourceCount: 0,
                    sourceTitles: []
                }
            ]
        };

        addMessage(conversationId, assistantMessage);
    }, [addMessage]);

    const updateMessage = useCallback((conversationId: string, messageId: string, updateFn: (message: ConversationMessageModel) => ConversationMessageModel) => {
        queryClient.setQueryData<ConversationModel>(AiQueryKeys.GetConversation(conversationId), old => {
            const messages = old!.messages.map<ConversationMessageModel>(item => {
                return item.id === messageId ? updateFn(item) : item;
            });

            return { ...old!, messages };
        });
    }, [queryClient]);

    return {
        addUserMessage,
        addTargetedReplyMessage,
        addFileMessage,
        addAssistantMessage,
        updateMessage
    };
};
